import React, { useState, useEffect, useCallback } from 'react';
import Header from '../common/Header';
import Footer from '../common/Footer';
import FormModal from '../common/FormModal';
import ConfirmModal from '../common/ConfirmModal';
import FormField from '../common/FormField';
import FormGrid from '../common/FormGrid';
import { useToast } from '../../contexts/ToastContext';
import {
  FaPlus, FaEdit, FaTrash, FaSearch, FaUsers,
  FaMotorcycle, FaConciergeBell, FaPhone, FaMapMarkerAlt
} from 'react-icons/fa';
import { getApiUrl } from '../../utils/api';
import './Meseros.css';

const Meseros = () => {
  const toast = useToast();
  const [meseros, setMeseros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('todos'); // todos | mesero | repartidor
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [seleccionado, setSeleccionado] = useState(null);
  const [aEliminar, setAEliminar] = useState(null);

  const [form, setForm] = useState({
    nombre: '',
    numeroMesero: '',
    calle: '',
    numero: '',
    fraccionamiento: '',
    cp: '',
    telefono: '',
    celular: '',
    mesero: '1',
    repartidor: '0'
  });

  // ═══════════════════════════════════════════════════════════════
  // API
  // ═══════════════════════════════════════════════════════════════
  const apiCall = useCallback(async (tipo, extra = {}) => {
    const res = await fetch(getApiUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tipo, ...extra })
    });
    return res.json();
  }, []);

  const cargarMeseros = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiCall('getMeserosCompleto');
      if (res.success) {
        setMeseros(res.data || []);
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error('Error al cargar meseros');
    } finally {
      setLoading(false);
    }
  }, [apiCall, toast]);

  useEffect(() => { cargarMeseros(); }, [cargarMeseros]);

  // ═══════════════════════════════════════════════════════════════
  // CRUD
  // ═══════════════════════════════════════════════════════════════
  const abrirNuevo = () => {
    setSeleccionado(null);
    setForm({
      nombre: '', numeroMesero: '', calle: '', numero: '',
      fraccionamiento: '', cp: '', telefono: '', celular: '',
      mesero: '1', repartidor: '0'
    });
    setShowModal(true);
  };

  const abrirEditar = (m) => {
    setSeleccionado(m);
    setForm({
      nombre: m.nombre || '',
      numeroMesero: m.numeroMesero || '',
      calle: m.calle || '',
      numero: m.numero || '',
      fraccionamiento: m.fraccionamiento || '',
      cp: m.cp || '',
      telefono: m.telefono || '',
      celular: m.celular || '',
      mesero: String(m.mesero) === '1' ? '1' : '0',
      repartidor: String(m.repartidor) === '1' ? '1' : '0'
    });
    setShowModal(true);
  };

  const guardar = async () => {
    if (!form.nombre) {
      toast.error('El nombre es obligatorio');
      return;
    }

    setGuardando(true);
    try {
      const tipo = seleccionado ? 'actualizarMesero' : 'crearMesero';
      const payload = { ...form };
      if (seleccionado) payload.idMesero = seleccionado.idMesero;

      const res = await apiCall(tipo, payload);
      if (res.success) {
        toast.success(res.message);
        setShowModal(false);
        cargarMeseros();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error('Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const confirmarEliminar = (m) => {
    setAEliminar(m);
    setShowDeleteModal(true);
  };

  const eliminar = async () => {
    try {
      const res = await apiCall('eliminarMesero', { idMesero: aEliminar.idMesero });
      if (res.success) {
        toast.success(res.message);
        setShowDeleteModal(false);
        cargarMeseros();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // FILTROS
  // ═══════════════════════════════════════════════════════════════
  const meserosFiltrados = meseros.filter(m => {
    // Filtro por tipo
    if (filtroTipo === 'mesero' && String(m.mesero) !== '1') return false;
    if (filtroTipo === 'repartidor' && String(m.repartidor) !== '1') return false;

    // Filtro por busqueda
    if (!busqueda) return true;
    const q = busqueda.toLowerCase();
    return (m.nombre || '').toLowerCase().includes(q) ||
      String(m.numeroMesero).includes(q) ||
      (m.telefono || '').includes(q) ||
      (m.celular || '').includes(q);
  });

  // Conteos
  const totalMeseros = meseros.filter(m => String(m.mesero) === '1').length;
  const totalRepartidores = meseros.filter(m => String(m.repartidor) === '1').length;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const getTipoBadge = (m) => {
    const esMesero = String(m.mesero) === '1';
    const esRepartidor = String(m.repartidor) === '1';
    if (esMesero && esRepartidor) return 'ambos';
    if (esMesero) return 'mesero';
    if (esRepartidor) return 'repartidor';
    return 'ninguno';
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <div className="mes-container">
      <Header titulo="Meseros y Repartidores" color="#00BCD4" />

      <div className="mes-content">
        {/* Stats rápidas */}
        <div className="mes-stats">
          <div className="mes-stat-card mes-stat--total">
            <FaUsers />
            <div>
              <span className="mes-stat-num">{meseros.length}</span>
              <span className="mes-stat-label">Total</span>
            </div>
          </div>
          <div className="mes-stat-card mes-stat--mesero">
            <FaConciergeBell />
            <div>
              <span className="mes-stat-num">{totalMeseros}</span>
              <span className="mes-stat-label">Meseros</span>
            </div>
          </div>
          <div className="mes-stat-card mes-stat--repartidor">
            <FaMotorcycle />
            <div>
              <span className="mes-stat-num">{totalRepartidores}</span>
              <span className="mes-stat-label">Repartidores</span>
            </div>
          </div>
        </div>

        {/* Panel principal */}
        <div className="mes-panel">
          <div className="mes-toolbar">
            <div className="mes-toolbar-left">
              <div className="mes-search">
                <FaSearch className="mes-search-icon" />
                <input
                  type="text"
                  placeholder="Buscar por nombre, numero o telefono..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  className="mes-search-input"
                />
              </div>
              <div className="mes-filtro-tipo">
                <button className={`mes-filtro-btn ${filtroTipo === 'todos' ? 'mes-filtro-btn--active' : ''}`} onClick={() => setFiltroTipo('todos')}>Todos</button>
                <button className={`mes-filtro-btn ${filtroTipo === 'mesero' ? 'mes-filtro-btn--active' : ''}`} onClick={() => setFiltroTipo('mesero')}>Meseros</button>
                <button className={`mes-filtro-btn ${filtroTipo === 'repartidor' ? 'mes-filtro-btn--active' : ''}`} onClick={() => setFiltroTipo('repartidor')}>Repartidores</button>
              </div>
            </div>
            <button className="mes-btn mes-btn--primary" onClick={abrirNuevo}>
              <FaPlus /> Nuevo
            </button>
          </div>

          {loading ? (
            <div className="mes-loading">Cargando...</div>
          ) : (
            <div className="mes-table-wrap">
              <table className="mes-tabla">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Nombre</th>
                    <th>Tipo</th>
                    <th>Telefono</th>
                    <th>Celular</th>
                    <th>Direccion</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {meserosFiltrados.length === 0 ? (
                    <tr><td colSpan="7" className="mes-empty">No se encontraron registros</td></tr>
                  ) : (
                    meserosFiltrados.map(m => {
                      const tipo = getTipoBadge(m);
                      const dir = [m.calle, m.numero, m.fraccionamiento].filter(Boolean).join(', ');
                      return (
                        <tr key={m.idMesero}>
                          <td className="mes-num">{m.numeroMesero}</td>
                          <td className="mes-nombre">{m.nombre}</td>
                          <td>
                            {tipo === 'ambos' && (
                              <><span className="mes-badge mes-badge--mesero">Mesero</span>{' '}<span className="mes-badge mes-badge--repartidor">Repartidor</span></>
                            )}
                            {tipo === 'mesero' && <span className="mes-badge mes-badge--mesero">Mesero</span>}
                            {tipo === 'repartidor' && <span className="mes-badge mes-badge--repartidor">Repartidor</span>}
                            {tipo === 'ninguno' && <span className="mes-badge mes-badge--ninguno">Sin tipo</span>}
                          </td>
                          <td>{m.telefono || '-'}</td>
                          <td>{m.celular || '-'}</td>
                          <td className="mes-dir">{dir || '-'}</td>
                          <td className="mes-acciones">
                            <button className="mes-btn-icon mes-btn-icon--edit" title="Editar" onClick={() => abrirEditar(m)}>
                              <FaEdit />
                            </button>
                            <button className="mes-btn-icon mes-btn-icon--delete" title="Eliminar" onClick={() => confirmarEliminar(m)}>
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Footer />

      {/* ═══ MODAL CREAR/EDITAR ═══ */}
      <FormModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={seleccionado ? 'Editar Mesero/Repartidor' : 'Nuevo Mesero/Repartidor'}
        onSave={guardar}
        isSaving={guardando}
        saveLabel={seleccionado ? 'Actualizar' : 'Crear'}
        size="medium"
      >
        <FormGrid columns={2}>
          <FormField
            label="Nombre completo"
            name="nombre"
            value={form.nombre}
            onChange={handleChange}
            required
            placeholder="Nombre del mesero/repartidor"
          />
          <FormField
            label="Numero"
            name="numeroMesero"
            type="number"
            value={form.numeroMesero}
            onChange={handleChange}
            placeholder="Auto si se deja vacio"
            min={1}
          />
          <FormField
            label="Telefono"
            name="telefono"
            type="tel"
            value={form.telefono}
            onChange={handleChange}
            placeholder="Telefono fijo"
            icon={<FaPhone />}
          />
          <FormField
            label="Celular"
            name="celular"
            type="tel"
            value={form.celular}
            onChange={handleChange}
            placeholder="Celular"
            icon={<FaPhone />}
          />
        </FormGrid>

        <div className="mes-form-section-title">
          <FaMapMarkerAlt /> Direccion
        </div>
        <FormGrid columns={3}>
          <FormField
            label="Calle"
            name="calle"
            value={form.calle}
            onChange={handleChange}
            placeholder="Calle"
          />
          <FormField
            label="Numero"
            name="numero"
            value={form.numero}
            onChange={handleChange}
            placeholder="# Ext/Int"
          />
          <FormField
            label="C.P."
            name="cp"
            value={form.cp}
            onChange={handleChange}
            placeholder="C.P."
          />
        </FormGrid>
        <FormGrid columns={1}>
          <FormField
            label="Colonia / Fraccionamiento"
            name="fraccionamiento"
            value={form.fraccionamiento}
            onChange={handleChange}
            placeholder="Colonia o fraccionamiento"
          />
        </FormGrid>

        <div className="mes-tipo-section">
          <span className="mes-tipo-title">Tipo de empleado:</span>
          <div className="mes-tipo-checks">
            <label className={`mes-tipo-check ${form.mesero === '1' ? 'mes-tipo-check--on' : ''}`}>
              <input
                type="checkbox"
                checked={form.mesero === '1'}
                onChange={() => setForm(prev => ({ ...prev, mesero: prev.mesero === '1' ? '0' : '1' }))}
              />
              <FaConciergeBell /> Mesero
            </label>
            <label className={`mes-tipo-check ${form.repartidor === '1' ? 'mes-tipo-check--on' : ''}`}>
              <input
                type="checkbox"
                checked={form.repartidor === '1'}
                onChange={() => setForm(prev => ({ ...prev, repartidor: prev.repartidor === '1' ? '0' : '1' }))}
              />
              <FaMotorcycle /> Repartidor
            </label>
          </div>
        </div>
      </FormModal>

      {/* ═══ CONFIRM DELETE ═══ */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={eliminar}
        title="Eliminar Mesero/Repartidor"
        message={`Estas seguro de eliminar a "${aEliminar?.nombre}" (#${aEliminar?.numeroMesero})? Esta accion no se puede deshacer.`}
        confirmLabel="Eliminar"
        type="danger"
      />
    </div>
  );
};

export default Meseros;
